/**
 * @file        src/lib/imageVariants.ts
 * @description Single source of truth for the responsive-image standard (JCK-IMG-RESP-1): the width
 *              ladder, the per-width variant formats, the encode quality settings, the variant-generation
 *              counter, and the pure helpers that build variant URLs and <picture> sources. A pure
 *              config+logic module — importable by BOTH the write-time generator (photoIngest) and the
 *              render component (<ResponsiveImage>) without pulling in sharp, fs, fetch, or Next APIs.
 * @rule        SoT (I7) — the width ladder, formats, and quality live ONLY here. No "magic" widths,
 *              formats, or quality numbers anywhere else in the codebase; every consumer imports from
 *              this module. A later component or script that hardcodes a width is a violation.
 * @rule        No upscaling (I9) — widthsForIntrinsic must never emit a width greater than the master's
 *              intrinsic width; srcset descriptors equal the REAL file widths, or the browser picks wrong.
 * @universal   knowledge/universals/tools.md#imageVariants
 */

// === TYPES =================================================================

/** Per-format encode settings. AVIF carries an `effort` knob; WebP does not. Internal — used to type VARIANT_QUALITY. */
interface VariantQuality {
  avif: { quality: number; effort: number };
  webp: { quality: number };
}

/**
 * The four fields a <picture> render needs for one photo.
 * `avif`/`webp` are srcset strings ("url 384w, url 640w, …"); `jpeg` is the bare master URL
 * (the `<img src>` / ultimate fallback); `widths` is the real width list so the render can pick a default.
 */
export interface PictureSources {
  avif: string;
  webp: string;
  jpeg: string;
  widths: number[];
}

// === CONSTANTS =============================================================

/** The width ladder (px), ascending. The only place these numbers exist. */
export const IMAGE_WIDTHS = [384, 640, 828, 1200, 2048] as const;

/**
 * The formats that get a per-width ladder, in <source> preference order (AVIF first, then WebP).
 * JPEG is NOT laddered — the single JPEG master is the fallback, so it is absent from this list.
 */
export const VARIANT_FORMATS = ['avif', 'webp'] as const;

/** Encode quality per laddered format. */
export const VARIANT_QUALITY: VariantQuality = {
  avif: { quality: 55, effort: 4 },
  webp: { quality: 80 },
};

/**
 * Variant-generation counter. Bumped in a future prompt whenever the ladder/formats/quality change,
 * so regenerated variant files cache-bust cleanly. Integer, starts at 1.
 */
export const VARIANT_GEN = 1;

// === HELPERS ===============================================================

/**
 * Strip a trailing `.jpg`/`.jpeg` (case-insensitive) from a master URL to get its base key.
 * @input  masterUrl — the "/storage/catalog/{slug}/{seq}.jpg" master path.
 * @output the same string without the trailing jpeg extension.
 * @edge   no jpeg extension → returns the input unchanged (`"/a/b"` → `"/a/b"`).
 */
export function masterBaseKey(masterUrl: string): string {
  return masterUrl.replace(/\.jpe?g$/i, '');
}

/**
 * The widths to actually generate/serve for a master `intrinsicWidth` px wide: every ladder width
 * strictly less than the intrinsic width, plus the intrinsic width itself; ascending, de-duplicated.
 * @input  intrinsicWidth — the real pixel width of the JPEG master.
 * @output ascending width list, never any value greater than `intrinsicWidth` (no upscaling, I9).
 * @edge   `intrinsicWidth <= 0` or non-finite → `[]`.
 */
export function widthsForIntrinsic(intrinsicWidth: number): number[] {
  if (!Number.isFinite(intrinsicWidth) || intrinsicWidth <= 0) return [];
  const below = IMAGE_WIDTHS.filter((w) => w < intrinsicWidth);
  return Array.from(new Set([...below, intrinsicWidth]));
}

/**
 * Build one variant file URL from a base key, a width, and a format extension.
 * @input  baseKey (from masterBaseKey), width (px), ext ('avif' | 'webp' | …).
 * @output `` `${baseKey}-${width}.${ext}` `` — e.g. `"/storage/catalog/x/3-384.avif"`.
 * @edge   purely string composition; performs no validation on its arguments.
 */
export function variantUrl(baseKey: string, width: number, ext: string): string {
  return `${baseKey}-${width}.${ext}`;
}

/**
 * Build the full set of <picture> sources for one photo.
 * @input  masterUrl (the JPEG master path), intrinsicWidth (its real pixel width).
 * @output a PictureSources object (avif/webp srcset strings, jpeg master, real widths).
 * @edge   `intrinsicWidth <= 0` or non-finite → `null` (caller falls back to the bare master).
 */
export function buildPictureSources(masterUrl: string, intrinsicWidth: number): PictureSources | null {
  if (!Number.isFinite(intrinsicWidth) || intrinsicWidth <= 0) return null;
  const base = masterBaseKey(masterUrl);
  const widths = widthsForIntrinsic(intrinsicWidth);
  const srcsetFor = (ext: string): string =>
    widths.map((w) => `${variantUrl(base, w, ext)} ${w}w`).join(', ');
  return {
    avif: srcsetFor('avif'),
    webp: srcsetFor('webp'),
    jpeg: masterUrl,
    widths,
  };
}
